/**
 * Data layer for `clevertap-integration`. Thin typed fetch wrappers over /api/v1/clevertap-integration.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ClevertapIntegrationRow {
	id: number;
	external_id: string;
	account_id: string;
	region: string;
	status: string;
	created_at: number;
}

export interface ListClevertapIntegrationResponse {
	items: ClevertapIntegrationRow[];
}

export interface CreateClevertapIntegrationBody {
	external_id: string;
	account_id: string;
	passcode: string;
	region: string;
}

export interface CreateClevertapIntegrationResponse {
	ok: boolean;
	external_id: string;
	region: string;
	created_at: number;
}

const BASE = "/api/v1/clevertap-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListClevertapIntegrationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListClevertapIntegrationResponse>(res);
}

export async function create(
	body: CreateClevertapIntegrationBody,
): Promise<CreateClevertapIntegrationResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateClevertapIntegrationResponse>(res);
}

export const clevertapIntegrationQueryKeys = {
	all: ["clevertap-integration"] as const,
	list: () => [...clevertapIntegrationQueryKeys.all] as const,
	detail: (id: string) => [...clevertapIntegrationQueryKeys.all, id] as const,
};

export const CLEVERTAP_REGIONS = [
	{ value: "us1", label: "US (us1)" },
	{ value: "eu1", label: "EU (eu1)" },
	{ value: "in1", label: "India (in1)" },
	{ value: "sg1", label: "Singapore (sg1)" },
	{ value: "aps3", label: "Asia Pacific (aps3)" },
	{ value: "mec1", label: "Middle East (mec1)" },
] as const;
